import { type ReactNode } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAutoSave } from '@/shared/hooks';
import { cn } from '@/shared/lib/utils';
import { Button } from '../button';

export interface AutoSaveFormActionBarProps {
  /** Persists pending changes. Resolves once the save settles; used by both autosave and the manual button. */
  onSave: () => Promise<void>;
  /** Whether there are unsaved local changes. */
  isDirty: boolean;
  /** Monotonic token that changes on every edit — resets the autosave debounce timer. */
  changeToken: number;
  /** Leaves the form. Pending changes are flushed first so nothing is lost on the way out. */
  onBack: () => void;
  /** Debounce delay before autosave fires. Default 1500 ms. */
  delayMs?: number;
  /** Disables autosave and the manual save button (e.g. read-only / inherited schema). */
  disabled?: boolean;
  /** Extra actions rendered at the far left (e.g. Save & Approve / Publish). */
  extraActions?: ReactNode;
  className?: string;
}

/**
 * Autosave variant of {@link FormActionBar}: debounced continuous saving via {@link useAutoSave}, a live
 * status note (Ukládám… / neuložené změny / uloženo), a manual **Uložit** (force-flush) and **Zpět**
 * (flush-then-leave). Kept separate from the generic {@link FormActionBar} — autosave semantics (debounce,
 * status, no „Uložit & zpět") differ enough that folding them into the shared bar would burden every
 * non-autosave agenda. Agendas that save continuously opt into this bar instead.
 */
export function AutoSaveFormActionBar({
  onSave,
  isDirty,
  changeToken,
  onBack,
  delayMs = 1500,
  disabled = false,
  extraActions,
  className,
}: AutoSaveFormActionBarProps) {
  const { t } = useTranslation('sharedUi');
  const { isSaving, triggerSave } = useAutoSave({
    flush: onSave,
    isDirty: isDirty && !disabled,
    changeToken,
    delayMs,
  });

  const status = isSaving ? 'saving' : isDirty ? 'dirty' : 'saved';

  const handleBack = async () => {
    if (isDirty && !disabled) await triggerSave();
    onBack();
  };

  return (
    <footer
      className={cn(
        'bg-phx-surface-2 border-phx-border-2 px-phx-5 gap-phx-4 flex shrink-0 items-center border-t py-2',
        className
      )}
    >
      {extraActions ? <div className="flex items-center">{extraActions}</div> : null}
      <span className="flex-1" />
      <span className="text-phx-text-muted text-phx-sm inline-flex min-w-0 items-center gap-1.5 text-center">
        {status === 'saving' ? (
          <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />
        ) : status === 'saved' ? (
          <Check aria-hidden className="text-phx-success h-3.5 w-3.5" />
        ) : (
          <span aria-hidden className="bg-phx-warning h-1.5 w-1.5 rounded-full" />
        )}
        {t(`formActionBar.autosave.${status}`)}
      </span>
      <span className="flex-1" />
      <div className="gap-phx-2 flex items-center">
        <Button variant="ghost" size="sm" onClick={() => void handleBack()} disabled={isSaving}>
          {t('formActionBar.back')}
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={() => void triggerSave()}
          loading={isSaving}
          disabled={disabled || !isDirty}
        >
          {t('formActionBar.save')}
        </Button>
      </div>
    </footer>
  );
}
